import { Component, OnInit, Inject, signal, DestroyRef, inject, computed } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { MatTabGroup, MatTab, MatTabLabel } from '@angular/material/tabs';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  UntypedFormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
  deleteField,
  getDoc,
  addDoc,
} from 'firebase/firestore';
import { CustomValidators } from 'angular-custom-validators';
import { StorageReference, getBytes, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { PDFDocument } from 'pdf-lib';
import moment from 'moment';
import {
  PaymentMethodsConfig,
  UserWithWarnings,
  EmergencyPhoneInfo,
  DataStoreExport,
  Particularity,
} from 'src/app/interfaces';
import { PaymentMethod, Role } from 'src/app/enums';
import { generateRandomString, openSnackbar, ucFirst } from '../../../globals';
import { getObjectDifferences } from 'ts-object-difference';
import { WarningDialog } from 'src/app/global-dialogs/warning/warning.component';
import { getAuth } from 'firebase/auth';
import { dataStore, db, functions, storage } from 'src/app/app.component';
import { AddressValidationFlowService } from 'src/app/services/address-validation-flow.service';
import { lastValueFrom, take } from 'rxjs';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatChipSet, MatChip } from '@angular/material/chips';
import { NgClass } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel, MatSuffix, MatError, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSelect, MatOption } from '@angular/material/select';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { EmergencyNumbersDialogComponent } from './emergency-numbers/emergency-numbers.component';

export interface DialogData {
  publicSettings: any;
  generalSettings: any;
  roles: Role[];
  user?: UserWithWarnings;
}

type NotificationMainControl = 'notifyByEmail' | 'notifyBySMS';
type NotificationSubControl = 'Confirmed' | 'Changed' | 'Deleted' | 'Reminder';
type NotificationRideControl =
  | `notifyByEmailRide${NotificationSubControl}`
  | `notifyBySMSRide${NotificationSubControl}`;

type TabKey = 'personal' | 'ride' | 'payment' | 'notifications';

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss'],
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    MatDialogActions,
    NgClass,
    MatIcon,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatIconButton,
    MatSuffix,
    MatError,
    MatTooltip,
    MatSelect,
    MatOption,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepicker,
    MatButton,
    MatCheckbox,
    MatDialogClose,
    MatProgressSpinner,
    MatTabGroup,
    MatTab,
    MatTabLabel,
    MatHint,
    MatChipSet,
    MatChip,
  ],
})
export class UserComponent implements OnInit {
  functions: any = functions;

  dataStore: DataStoreExport = dataStore;
  organisationId: string = dataStore.selectedOrganisation.id;

  paymentMethod = PaymentMethod;
  currentUserRoles: Role[] = this.data.roles;
  userForm!: UntypedFormGroup;
  newUser: boolean = this.data.user ? false : true;
  loading = signal(false);
  loadingData = signal(true);
  askGender: boolean = false;
  askBirthDate: boolean = false;
  isEmailSignInRequired = true;
  isPhoneSignInRequired = false;
  emailNotificationsParticipants: boolean = false;
  smsNotificationsParticipants: boolean = false;
  planningComments: boolean = this.data.generalSettings.pCommentsToggle ? true : false;
  driverComments: boolean = this.data.generalSettings.dCommentsToggle ? true : false;
  dialogTitle: string = this.newUser
    ? 'Nieuwe passagier'
    : `${this.data.user?.name ?? ''} (${this.data.user?.credits ?? 0})`;
  planArray = signal<any[]>([]);
  particularitiesArray: Particularity[] = dataStore.selectedOrganisation.particularities ?? [];
  productArray = signal<any[]>([]);
  sepaPath = signal<string>(this.data.user?.sepaPath ?? 'sepa_authorization.pdf');
  sepaMandateId = signal<string | null>(this.data.user?.sepaMandateId ?? null);
  emergencyPhoneNumbers = signal<EmergencyPhoneInfo[]>([]);
  roles: Role[] = [];
  ucFirst = ucFirst;
  Role = Role;
  roleLabels: Record<string, string> = {
    [Role.Administrator]: 'Administrator',
    [Role.Planner]: 'Planner',
    [Role.RidePlanner]: 'Ritplanner',
    [Role.Driver]: 'Chauffeur',
    [Role.CoDriver]: 'Bijrijder',
    [Role.Other]: 'Overige',
  };
  isOnlyRidePlanner = signal(false);
  isLookingUp = signal(false);
  addressNotFound = signal(false);
  private readonly destroyRef = inject(DestroyRef);
  selectedIndex = signal(0);
  maxTabIndex = computed(() => (this.isOnlyRidePlanner() ? 2 : 3));
  private readonly tabLabels: Record<TabKey, string> = {
    personal: 'persoonsgegevens',
    ride: 'ritgegevens',
    payment: 'betaalgegevens',
    notifications: 'notificaties',
  };
  private readonly tabControls: Record<TabKey, string[]> = {
    personal: [
      'name',
      'email',
      'password',
      'phoneNumber',
      'postal',
      'houseNumber',
      'streetName',
      'city',
      'birthDate',
      'gender',
    ],
    ride: ['vehicleEntryDuration', 'vehicleExitDuration'],
    payment: ['paymentMethod', 'currentPlan', 'iban'],
    notifications: ['smsNotificationPhoneNumber'],
  };

  nextTab() {
    if (this.selectedIndex() < this.maxTabIndex()) {
      this.selectedIndex.update((i) => i + 1);
    }
  }

  prevTab() {
    if (this.selectedIndex() > 0) {
      this.selectedIndex.update((i) => i - 1);
    }
  }

  showTabAttention(tab: TabKey): boolean {
    return this.hasTabValidationIssues(tab) || this.hasTabDomainWarnings(tab);
  }

  getTabAttentionTooltip(tab: TabKey): string {
    const tabLabel = this.tabLabels[tab];
    if (this.hasTabValidationIssues(tab)) {
      return `Er zijn validatiefouten bij de ${tabLabel}`;
    }
    if (this.hasTabDomainWarnings(tab)) {
      return `Er zijn 1 of meerdere meldingen bij de ${tabLabel}`;
    }
    return '';
  }

  hasTabValidationIssues(tab: TabKey): boolean {
    return this.tabControls[tab].some((controlName) => {
      const control = this.userForm?.controls?.[controlName];
      return Boolean(
        control && control.enabled && control.invalid && (control.touched || control.dirty),
      );
    });
  }

  private hasTabDomainWarnings(tab: TabKey): boolean {
    if (tab === 'payment') {
      return (this.data.user?.warnings?.length ?? 0) > 0;
    }
    return false;
  }

  openEmergencyNumbersDialog() {
    this.dialog
      .open(EmergencyNumbersDialogComponent, {
        width: '800px',
        data: {
          emergencyPhoneNumbers: this.emergencyPhoneNumbers(),
          isOnlyRidePlanner: this.isOnlyRidePlanner(),
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          this.emergencyPhoneNumbers.set(result);
        }
      });
  }

  getRoleLabel(role: Role): string {
    const labels: Record<string, string> = {
      [Role.Administrator]: 'Administrator',
      [Role.Driver]: 'Chauffeur',
      [Role.CoDriver]: 'Bijrijder',
      [Role.Planner]: 'Planner',
      [Role.RidePlanner]: 'Ritplanner',
    };
    return labels[role] || role;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<UserComponent>,
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    private addressValidationFlowService: AddressValidationFlowService,
  ) {}

  async ngOnInit() {
    const isSuperUser = dataStore.rootUser?.rights === 'admin';
    const isOnlyRidePlanner =
      !isSuperUser &&
      this.currentUserRoles?.includes(Role.RidePlanner) &&
      !this.currentUserRoles?.includes(Role.Planner) &&
      !this.currentUserRoles?.includes(Role.Administrator);
    this.isOnlyRidePlanner.set(isOnlyRidePlanner);

    console.log('data:', this.data);
    this.userForm = this.fb.group({
      email: ['', [Validators.required, CustomValidators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      name: [, Validators.required],
      streetName: [, Validators.required],
      houseNumber: [, [Validators.required, CustomValidators.numberInput(false, false, 0)]],
      postal: [, [CustomValidators.postal, Validators.required]],
      city: [, Validators.required],
      phoneNumber: ['', [CustomValidators.phone]],
      phoneNumberHome: ['', [CustomValidators.phone]],
      houseNumberAddition: [],
      currentPlan: ['none'],
      cardId: [, CustomValidators.cannotContainSpace],
      remarks: [],
      hiddenRemarks: [],
      commentsForDriver: [],
      rideProductId: [],
      particularityIds: [[]],
      paymentMethod: [PaymentMethod.NONE],
    });
    this.applySignInMethodValidation();
    this.userForm.controls.streetName.disable();
    this.userForm.controls.city.disable();

    await this.getPlans();
    await this.getProducts();

    if (!this.newUser && this.data.user) {
      const user = this.data.user;
      this.applySignInMethodValidation(user);
      this.userForm.controls.streetName.enable();
      this.userForm.controls.city.enable();
      if (this.isEmailSignInRequired) {
        this.userForm.controls.email.disable();
      } else {
        this.userForm.controls.email.enable();
      }
      if (this.isPhoneSignInRequired) {
        this.userForm.controls.phoneNumber.disable();
      } else {
        this.userForm.controls.phoneNumber.enable();
      }
      this.userForm.removeControl('password');
      this.userForm.patchValue(user);
      if (!this.userForm.controls.paymentMethod.value) {
        this.userForm.patchValue({ paymentMethod: PaymentMethod.NONE }, { emitEvent: false });
      }
      if (!this.userForm.controls.currentPlan.value) {
        this.userForm.patchValue({ currentPlan: 'none' }, { emitEvent: false });
      }
      if (user.emergencyPhoneNumbers) {
        user.emergencyPhoneNumbers.forEach((phoneInfo) => {
          this.emergencyPhoneNumbers.update((phones) => [
            ...phones,
            { ...phoneInfo, editing: false },
          ]);
        });
      }
      const currentPlanId = user.currentPlan?.id;
      if (currentPlanId) {
        this.planArray().find((plan) => {
          if (plan.id === currentPlanId) {
            this.userForm.patchValue({ currentPlan: plan.id });
          }
        });
      }

      if (!user.paymentMethod) {
        this.userForm.controls.currentPlan.disable();
      }

      if (user.rideProductId) {
        this.productArray().find((product) => {
          if (product.id === user.rideProductId) {
            this.userForm.patchValue({ rideProductId: product.id });
          }
        });
      } else {
        this.userForm.patchValue({ rideProductId: 'none' });
      }

      const particularitiesArray: string[] = structuredClone(
        this.data?.user?.particularityIds ?? [],
      );
      this.userForm.controls.particularityIds.setValue(particularitiesArray);

      if (user.roles && user.roles.length > 0 && !user.inactive) {
        // This seems to be for displaying a label, but let's keep it simple for now or map it
        this.roles = user.roles;
      }
    } else {
      this.userForm.patchValue({ currentPlan: 'none' });
      this.userForm.controls.currentPlan.disable();
      this.userForm.patchValue({ rideProductId: 'none' });
    }

    this.getPublicSettingsFormControls();
    this.getPaidFunctionalitiesFormControls();

    if (!this.isOnlyRidePlanner()) {
      const addressLookup = this.addressValidationFlowService.connect(
        this.userForm,
        this.destroyRef,
      );
      this.isLookingUp = addressLookup.isLookingUp;
      this.addressNotFound = addressLookup.addressNotFound;
    }

    if (this.isOnlyRidePlanner()) {
      Object.keys(this.userForm.controls).forEach((key) => {
        if (key !== 'particularityIds') {
          this.userForm.controls[key].disable();
        }
      });
    }

    this.userForm.controls.paymentMethod.valueChanges.subscribe((value) => {
      if (this.isOnlyRidePlanner()) return;
      if (value === PaymentMethod.SEPA) {
        this.userForm.controls.iban?.enable();
      } else {
        this.userForm.controls.iban?.disable();
      }
      if (value !== PaymentMethod.NONE && this.currentUserRoles?.includes(Role.Administrator)) {
        this.userForm.controls.currentPlan.enable();
      } else {
        this.userForm.controls.currentPlan.setValue('none');
        this.userForm.controls.currentPlan.disable();
      }
    });

    if (this.data.publicSettings.emailNotificationsParticipants) {
      this.userForm.controls.email.valueChanges.subscribe(() => {
        if (this.isOnlyRidePlanner()) return;
        if (this.userForm.controls.email.valid && this.userForm.controls.email.value) {
          this.userForm.controls.notifyByEmail.enable();
          this.userForm.controls.notifyByEmailRideConfirmed.enable();
          this.userForm.controls.notifyByEmailRideChanged.enable();
          this.userForm.controls.notifyByEmailRideDeleted.enable();
          this.userForm.controls.notifyByEmailRideReminder.enable();
        } else {
          this.userForm.controls.notifyByEmail.disable();
          this.userForm.controls.notifyByEmailRideConfirmed.disable();
          this.userForm.controls.notifyByEmailRideChanged.disable();
          this.userForm.controls.notifyByEmailRideDeleted.disable();
          this.userForm.controls.notifyByEmailRideReminder.disable();
        }
      });
    }

    if (this.data.publicSettings.smsNotificationsParticipants) {
      this.userForm.controls.smsNotificationPhoneNumber.valueChanges.subscribe(() => {
        if (this.isOnlyRidePlanner()) return;
        if (
          this.userForm.controls.smsNotificationPhoneNumber.valid &&
          this.userForm.controls.smsNotificationPhoneNumber.value
        ) {
          this.userForm.controls.notifyBySMS.enable();
          this.userForm.controls.notifyBySMSRideConfirmed.enable();
          this.userForm.controls.notifyBySMSRideChanged.enable();
          this.userForm.controls.notifyBySMSRideDeleted.enable();
          this.userForm.controls.notifyBySMSRideReminder.enable();
        } else {
          this.userForm.controls.notifyBySMS.disable();
          this.userForm.controls.notifyBySMSRideConfirmed.disable();
          this.userForm.controls.notifyBySMSRideChanged.disable();
          this.userForm.controls.notifyBySMSRideDeleted.disable();
          this.userForm.controls.notifyBySMSRideReminder.disable();
        }
      });
    }

    if (!this.newUser) {
      this.userForm.updateValueAndValidity({ emitEvent: false });
      this.userForm.markAllAsTouched();
    }

    this.loadingData.set(false);
  }

  async save() {
    if (this.userForm.valid) {
      this.loading.set(true);

      if (this.userForm.controls.cardId.value) {
        const users = await getDocs(
          query(
            collection(db, `organisations/${this.organisationId}/users/`),
            where('cardId', '==', this.userForm.controls.cardId.value),
          ),
        );

        const isCardIdInUseByOther = users.docs.some((doc) => doc.id !== this.data.user?.id);

        if (isCardIdInUseByOther) {
          openSnackbar(this.snackBar, 'Dit kaartnummer is al in gebruik.');
          this.loading.set(false);
          return;
        }
      }

      const saveObj = this.userForm.value;
      if (!this.isOnlyRidePlanner()) {
        if (this.userForm.controls.iban?.status === 'DISABLED') {
          saveObj.iban = this.userForm.controls.iban.value;
        }
        if (this.userForm.controls.currentPlan?.status === 'DISABLED') {
          saveObj.currentPlan = this.userForm.controls.currentPlan.value;
        }
        if (this.userForm.controls.streetName?.status === 'DISABLED') {
          saveObj.streetName = this.userForm.controls.streetName.value;
        }
        if (this.userForm.controls.city?.status === 'DISABLED') {
          saveObj.city = this.userForm.controls.city.value;
        }
      }
      console.log('saveObj:', saveObj);

      Object.keys(saveObj).forEach((key) => {
        if (
          saveObj[key] === '' ||
          saveObj[key] === 'none' ||
          saveObj[key] === null ||
          saveObj[key] === undefined
        ) {
          if (this.newUser) {
            delete saveObj[key];
          } else {
            saveObj[key] = null;
          }
        }
      });

      delete saveObj.password;

      if (this.isOnlyRidePlanner()) {
        const particularityIds = saveObj.particularityIds;
        Object.keys(saveObj).forEach((key) => delete saveObj[key]);
        if (particularityIds !== undefined) {
          saveObj.particularityIds = particularityIds;
        }
      }

      if (saveObj.postal) {
        saveObj.postal = saveObj.postal.replace(/\s/g, '').toUpperCase();
      }

      if (saveObj.iban && saveObj.iban._methodName !== 'deleteField') {
        saveObj.iban = saveObj.iban.replace(/\s/g, '').toUpperCase();
      }

      if (saveObj.vehicleEntryDuration) {
        saveObj.vehicleEntryDuration = Number(saveObj.vehicleEntryDuration);
      }
      if (saveObj.vehicleExitDuration) {
        saveObj.vehicleExitDuration = Number(saveObj.vehicleExitDuration);
      }

      if (
        saveObj.smsNotificationPhoneNumber &&
        saveObj.smsNotificationPhoneNumber._methodName !== 'deleteField'
      ) {
        saveObj.smsNotificationPhoneNumber = saveObj.smsNotificationPhoneNumber.replace(
          /[\s-]/g,
          '',
        );
        if (saveObj.smsNotificationPhoneNumber.startsWith('0')) {
          saveObj.smsNotificationPhoneNumber = saveObj.smsNotificationPhoneNumber.replace(
            '0',
            '+31',
          );
        }
      }

      console.log('saveObj cleaned:', saveObj);
      if (!this.isOnlyRidePlanner()) {
        const resolvedSaveObj = await this.addressValidationFlowService.resolveAddress(
          saveObj,
          this.data.user ?? {},
          dataStore.paidFunctionalities.usesAutomaticPlanning === true,
          !this.newUser && !!this.data.user,
        );
        if (!resolvedSaveObj) {
          this.loading.set(false);
          return;
        }
        Object.assign(saveObj, resolvedSaveObj);
      }

      if (!this.isOnlyRidePlanner()) {
        saveObj.emergencyPhoneNumbers = this.emergencyPhoneNumbers().map((phoneInfo) => {
          const phoneNumber = phoneInfo.phoneNumber.replace(/[\s-]/g, '');
          return {
            name: phoneInfo.name,
            phoneNumber: phoneNumber.startsWith('0')
              ? phoneNumber.replace('0', '+31')
              : phoneNumber,
            description: phoneInfo.description,
          };
        });
      }

      if (this.data.user) {
        const editedUserSignInMethod = this.data.user.signInMethod ?? 'email';
        if (
          this.data.user.email !== saveObj.email &&
          saveObj.email !== undefined &&
          editedUserSignInMethod === 'email'
        ) {
          const httpUpdateUser = httpsCallable(this.functions, 'httpUpdateUser');
          try {
            const result: any = await httpUpdateUser({
              type: 'email',
              userId: this.data.user.id,
              orgId: this.organisationId,
              emailNew: saveObj.email,
              emailOld: this.data.user.email,
            });

            const errorCode = this.extractHttpUpdateUserErrorCode(result);
            if (errorCode) {
              this.showHttpUpdateUserError(errorCode);
              this.loading.set(false);
              return;
            }
          } catch (error) {
            openSnackbar(
              this.snackBar,
              'Er is iets misgegaan, probeer het later opnieuw of neem contact op met de beheerder.',
            );
            console.log('error', error);
            this.loading.set(false);
            return;
          }
        }
        if (
          this.data.user.phoneNumber !== saveObj.phoneNumber &&
          saveObj.phoneNumber !== undefined &&
          editedUserSignInMethod === 'phoneNumber'
        ) {
          let cleanedPhoneNew = String(saveObj.phoneNumber ?? '').replace(/[^\d+]/g, '');
          if (/^6\d{8}$/.test(cleanedPhoneNew)) {
            cleanedPhoneNew = `0${cleanedPhoneNew}`;
          }
          if (cleanedPhoneNew.startsWith('06')) {
            cleanedPhoneNew = `+316${cleanedPhoneNew.substring(2)}`;
          }
          if (!cleanedPhoneNew.startsWith('+')) {
            openSnackbar(this.snackBar, 'Voer een geldig telefoonnummer in.');
            this.loading.set(false);
            return;
          }
          saveObj.phoneNumber = cleanedPhoneNew;
          const httpUpdateUser = httpsCallable(this.functions, 'httpUpdateUser');
          try {
            const result: any = await httpUpdateUser({
              type: 'phoneNumber',
              userId: this.data.user.id,
              orgId: this.organisationId,
              phoneNew: cleanedPhoneNew,
              phoneOld: this.data.user.phoneNumber ?? this.data.user.phone,
            });

            const errorCode = this.extractHttpUpdateUserErrorCode(result);
            if (errorCode) {
              this.showHttpUpdateUserError(errorCode);
              this.loading.set(false);
              return;
            }
          } catch (error) {
            openSnackbar(
              this.snackBar,
              'Er is iets misgegaan, probeer het later opnieuw of neem contact op met de beheerder.',
            );
            console.log('error', error);
            this.loading.set(false);
            return;
          }
        }
      }

      if (saveObj.paymentMethod && saveObj.paymentMethod._methodName !== 'deleteField') {
        if (!saveObj.currentPlan || saveObj.currentPlan._methodName === 'deleteField') {
          openSnackbar(
            this.snackBar,
            'Je kunt alleen een betaalmethode instellen als je ook een betaalplan hebt ingevuld.',
          );
          this.loading.set(false);
          return;
        }
        if (
          saveObj.paymentMethod === 'SEPA' &&
          (!saveObj.iban || saveObj.iban._methodName === 'deleteField')
        ) {
          openSnackbar(
            this.snackBar,
            'Als een passagier gebruik wil maken van een SEPA machtigingsformulier dan moet het IBAN invoerveld verplicht ingevuld worden.',
          );
          this.loading.set(false);
          return;
        }
      }

      if (saveObj.currentPlan) {
        this.planArray().find((plan) => {
          if (plan.id === saveObj.currentPlan) {
            saveObj.currentPlan = plan;
          }
        });
      }

      if (saveObj.name) {
        saveObj.name = ucFirst(saveObj.name);
      }

      if (!this.newUser && this.data.user) {
        const existingUser = this.data.user;
        const differences = getObjectDifferences(existingUser, structuredClone(saveObj));
        if (differences) {
          await updateDoc(
            doc(db, `organisations/${this.organisationId}/users`, `${existingUser.id}`),
            {
              ...saveObj,
            },
          );
          const currentAuthUser = getAuth().currentUser;
          const logData = {
            date: new Date(),
            doneByUser: currentAuthUser?.email ?? 'unknown',
            doneByUid: currentAuthUser?.uid ?? 'unknown',
            doneWithRights:
              this.dataStore.rootUser?.rights === 'admin'
                ? 'superuser'
                : (this.dataStore.orgUser?.roles || []).join(','),
            doneToUid: existingUser.id,
            ...differences,
          };
          await addDoc(
            collection(db, `eventLogs/${this.organisationId}/changedUserSettings`),
            logData,
          );
        }
        this.dialogRef.close();
        openSnackbar(this.snackBar, 'De wijzigingen zijn opgeslagen.');
        return;
      } else {
        const httpAddUser = httpsCallable(this.functions, 'httpAddUser');
        try {
          const result: any = await httpAddUser({
            email: saveObj.email,
            password: this.userForm.controls.password.value,
            organisationId: this.organisationId,
            saveObj,
          });
          switch (result.data.message) {
            case 'succeed':
              this.dialogRef.close();
              return;
            case 'error-occured':
              openSnackbar(
                this.snackBar,
                'Er is iets misgegaan, probeer het later opnieuw of neem contact op met de beheerder.',
              );
              break;
            case 'auth/email-already-exists':
              openSnackbar(this.snackBar, 'Dit e-mailadres is al in gebruik.');
              break;
            default:
              openSnackbar(this.snackBar, 'Er is iets misgegaan.');
              break;
          }
        } catch (error) {
          console.log('error', error);
        }
      }
      this.loading.set(false);
    }
  }

  async getPlans() {
    const planCollection = await getDocs(
      collection(db, `organisations/${this.organisationId}/plans`),
    );
    planCollection.forEach((plan) => {
      const planData = plan.data();
      planData['id'] = plan.id;
      this.planArray.update((arr) => [...arr, planData]);
    });
  }

  async getProducts() {
    const productCollection = await getDocs(
      collection(db, `organisations/${this.organisationId}/products`),
    );
    productCollection.forEach((product) => {
      const productData = product.data();
      productData['id'] = product.id;
      this.productArray.update((arr) => [...arr, productData]);
    });
  }

  getPublicSettingsFormControls() {
    if (this.data.publicSettings.sepaEnabled) {
      this.userForm.addControl(
        'iban',
        new UntypedFormControl(
          {
            value: this.data.user?.iban ?? '',
            disabled: this.userForm.value.paymentMethod !== PaymentMethod.SEPA ? true : false,
          },
          [CustomValidators.iban],
        ),
      );
    }
    if (this.data.publicSettings.askBirthDay && !this.isOnlyRidePlanner()) {
      this.userForm.addControl('birthDate', new UntypedFormControl('', [Validators.required]));
      this.askBirthDate = true;
      if (this.data.user?.birthDate !== null && this.data.user?.birthDate !== undefined) {
        this.userForm.controls.birthDate.setValue(new Date(this.data.user.birthDate.toDate()));
      }
    }
    if (this.data.publicSettings.askGender) {
      this.userForm.addControl('gender', new UntypedFormControl('', [Validators.required]));
      this.askGender = true;
      if (this.data.user?.gender !== null && this.data.user?.gender !== undefined) {
        this.userForm.controls.gender.setValue(this.data.user.gender);
      }
    }

    if (this.data.publicSettings.emailNotificationsParticipants) {
      this.setupNotificationControls('Email');
      this.emailNotificationsParticipants = true;
    }
    if (this.data.publicSettings.smsNotificationsParticipants) {
      this.userForm.addControl(
        'smsNotificationPhoneNumber',
        new UntypedFormControl(this.data?.user?.smsNotificationPhoneNumber ?? '', [
          CustomValidators.phone,
        ]),
      );

      this.setupNotificationControls('SMS');
      this.smsNotificationsParticipants = true;
    }
  }

  setupNotificationControls(type: 'Email' | 'SMS') {
    const mainControl: NotificationMainControl = type === 'Email' ? 'notifyByEmail' : 'notifyBySMS';
    const subControls: NotificationSubControl[] = ['Confirmed', 'Changed', 'Deleted', 'Reminder'];
    const userNotificationValues = this.data.user as
      | Partial<Record<NotificationMainControl | NotificationRideControl, boolean>>
      | undefined;
    const disableCondition =
      (type === 'Email' && !this.userForm.controls.email.value) ||
      (type === 'SMS' && !this.userForm.controls.smsNotificationPhoneNumber.value);
    this.userForm.addControl(
      mainControl,
      new UntypedFormControl(
        {
          value: userNotificationValues?.[mainControl] ?? false,
          disabled: disableCondition,
        },
        [],
      ),
    );

    for (const controlKey of subControls) {
      const rideControl = `${mainControl}Ride${controlKey}` as NotificationRideControl;
      this.userForm.addControl(
        rideControl,
        new UntypedFormControl(
          {
            value: userNotificationValues?.[rideControl] ?? true,
            disabled: disableCondition,
          },
          [],
        ),
      );
    }
  }

  getPaidFunctionalitiesFormControls() {
    if (dataStore.paidFunctionalities.usesAutomaticPlanning) {
      this.userForm.addControl(
        'vehicleEntryDuration',
        new UntypedFormControl(0, [
          Validators.max(15),
          CustomValidators.numberInput(false, false, 0),
        ]),
      );
      this.userForm.addControl(
        'vehicleExitDuration',
        new UntypedFormControl(0, [
          Validators.max(15),
          CustomValidators.numberInput(false, false, 0),
        ]),
      );
      this.userForm.controls.vehicleEntryDuration.patchValue(
        this.data.user?.vehicleEntryDuration ??
          dataStore.generalSettings?.vehicleEntryDuration ??
          0,
      );
      this.userForm.controls.vehicleExitDuration.patchValue(
        this.data.user?.vehicleExitDuration ?? dataStore.generalSettings?.vehicleExitDuration ?? 0,
      );
    }
  }

  toggleParticularity(iconId: string) {
    const activeParticularities: string[] = this.userForm.value.particularityIds;
    if (activeParticularities.indexOf(iconId) !== -1) {
      const index = activeParticularities.indexOf(iconId);
      activeParticularities.splice(index, 1);
    } else {
      activeParticularities.push(iconId);
    }
    this.userForm.controls.particularityIds.setValue(activeParticularities);
  }

  enableField(field: string) {
    this.userForm.controls[field].enable();
  }

  private extractHttpUpdateUserErrorCode(result: any): string | null {
    const callableData = result?.data;
    if (!callableData) {
      return null;
    }

    if (callableData.status === 'error' && callableData.error) {
      return String(callableData.error?.code ?? callableData.error);
    }

    if (callableData.error) {
      return String(callableData.error?.code ?? callableData.error);
    }

    return null;
  }

  private showHttpUpdateUserError(errorCode: string) {
    if (errorCode === 'auth/email-already-exists') {
      openSnackbar(this.snackBar, 'Dit e-mailadres is al in gebruik.');
      return;
    }

    if (errorCode === 'auth/phone-number-already-exists') {
      openSnackbar(this.snackBar, 'Dit telefoonnummer is al in gebruik.');
      return;
    }

    openSnackbar(
      this.snackBar,
      'Er is iets misgegaan, probeer het later opnieuw of neem contact op met de beheerder.',
    );
  }

  private applySignInMethodValidation(user?: UserWithWarnings) {
    const signInMethod = user?.signInMethod ?? 'email';
    this.isPhoneSignInRequired = signInMethod === 'phoneNumber';
    this.isEmailSignInRequired = !this.isPhoneSignInRequired;

    this.userForm.controls.email.setValidators(
      this.isEmailSignInRequired
        ? [Validators.required, CustomValidators.email]
        : [CustomValidators.email],
    );
    this.userForm.controls.phoneNumber.setValidators(
      this.isPhoneSignInRequired
        ? [Validators.required, CustomValidators.phone]
        : [CustomValidators.phone],
    );

    this.userForm.controls.email.updateValueAndValidity({ emitEvent: false });
    this.userForm.controls.phoneNumber.updateValueAndValidity({ emitEvent: false });
  }

  async downloadSepa() {
    try {
      const userId = this.data.user?.id;
      if (!userId) {
        return;
      }
      const pathRef = ref(storage, this.sepaPath());

      if (this.sepaPath() !== 'sepa_authorization.pdf') {
        // sepaPath is not the default path, no need to fill an uploaded mandate file
        const downloadUrl = await getDownloadURL(pathRef);
        console.log(downloadUrl);
        window.open(downloadUrl, '_blank');
      } else {
        if (!this.sepaMandateId()) {
          const formattedDate = moment(new Date()).format('YYYYMMDDHHmmss');
          const orgNameMandate = this.data.generalSettings.name.substring(0, 3);
          const mandateId = `${orgNameMandate.toUpperCase()}${formattedDate}`;
          this.sepaMandateId.set(mandateId);
          await setDoc(
            doc(db, `/organisations/${this.organisationId}/users/${userId}`),
            {
              sepaMandateId: mandateId,
            },
            { merge: true },
          );
        }
        const paymentMethodsConfig = (
          await getDoc(doc(db, `payment-configs/${this.organisationId}`))
        ).data() as PaymentMethodsConfig;
        const mandateId = this.sepaMandateId();
        if (!mandateId) {
          return;
        }
        const pdfBytes = await this.fillPdfWithUserInfo(pathRef, mandateId, paymentMethodsConfig);
        const blob = new Blob([pdfBytes.buffer as BlobPart], {
          type: 'application/pdf',
        });
        const url = window.URL.createObjectURL(blob);
        window.open(url);
      }
    } catch (error) {
      console.log(error);
    }
  }

  async fillPdfWithUserInfo(
    pathRef: StorageReference,
    sepaMandateId: string,
    paymentMethodConfig: PaymentMethodsConfig,
  ) {
    const formBytes = await getBytes(pathRef);
    const pdfDoc = await PDFDocument.load(formBytes);
    const form = pdfDoc.getForm();
    const userFormData = this.userForm.value;

    // Set company fields
    form.getTextField('machtigingBedrijfsnaam').setText(paymentMethodConfig.creditor_name ?? '');
    form
      .getTextField('machtigingBedrijfsadres')
      .setText(paymentMethodConfig.creditor_address ?? '');
    form
      .getTextField('machtigingBedrijfspostcode')
      .setText(paymentMethodConfig.creditor_postal ?? '');
    form
      .getTextField('machtigingBedrijfsplaats')
      .setText(paymentMethodConfig.creditor_municipality ?? '');
    form.getTextField('machtigingBedrijfsland').setText(paymentMethodConfig.creditor_country ?? '');
    form
      .getTextField('machtigingBedrijfsIncassantID')
      .setText(paymentMethodConfig.creditor_id ?? '');
    form.getTextField('machtigingKenmerk').setText(sepaMandateId);
    form.getTextField('machtigingRedenBetaling').setText('Buurtvervoer Credits');
    form
      .getTextField('machtigingOndertekeningBedrijfsnaam')
      .setText(paymentMethodConfig.creditor_name ?? '');
    form.getTextField('machtigingAfschrijvenNaam').setText(paymentMethodConfig.creditor_name ?? '');

    // set user fields
    form.getTextField('machtigingNaam').setText(userFormData.name ?? '');
    let address =
      userFormData.streetName && userFormData.houseNumber
        ? `${userFormData.streetName} ${userFormData.houseNumber}`
        : '';
    if (address && userFormData.houseNumberAddition) {
      address = `${address} ${userFormData.houseNumberAddition}`;
    }
    form.getTextField('machtigingAdres').setText(address);
    form.getTextField('machtigingPostcode').setText(userFormData.postal ?? '');
    form.getTextField('machtigingPlaats').setText(userFormData.city ?? '');
    form.getTextField('machtigingLand').setText(paymentMethodConfig.creditor_country ?? '');

    const iban: string = userFormData.iban
      ? userFormData.iban.replace(/\s/g, '').toUpperCase()
      : '';

    form.getTextField('machtigingIBAN1').setText(iban.substring(0, 4));
    form.getTextField('machtigingIBAN2').setText(iban.substring(4, 8));
    form.getTextField('machtigingIBAN3').setText(iban.substring(8, 12));
    form.getTextField('machtigingIBAN4').setText(iban.substring(12, 16));
    form.getTextField('machtigingIBAN5').setText(iban.substring(16, 18));

    const pdfBytes = await pdfDoc.save({ updateFieldAppearances: false });
    return pdfBytes;
  }

  async openFileInput(htmlId: string) {
    const element = document.getElementById(htmlId);
    element?.click();
  }

  async uploadSepa(event: Event) {
    const userId = this.data.user?.id;
    if (!userId) {
      return;
    }
    const inputElement = event.target as HTMLInputElement | null;
    const dialogRef = this.dialog.open(WarningDialog, {
      width: '600px',
      data: {
        dialogType: 'uploadSepaMandateIdCheck',
        optionalData: { sepaMandateId: this.sepaMandateId() },
      },
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        try {
          const file = inputElement?.files?.[0];
          if (!file) {
            return;
          }
          const formattedDate = moment(new Date()).format('YYYY-MM-DD-HHmmss');
          const pathRef = ref(
            storage,
            `/organisations/${this.organisationId}/users/${userId}/sepa_authorization(${formattedDate}).pdf`,
          );
          console.log('pathref', pathRef);
          const result = await uploadBytes(pathRef, file);
          const sepaDownloadUrl = await getDownloadURL(pathRef);
          this.sepaPath.set(result.metadata.fullPath);
          console.log('result', result);
          await setDoc(
            doc(db, `/organisations/${this.organisationId}/users/${userId}`),
            {
              sepaSignatureDate: new Date(),
              sepaDownloadUrl: sepaDownloadUrl,
              sepaPath: result.metadata.fullPath,
            },
            { merge: true },
          );
        } catch (error) {
          console.log(error);
        }
      } else {
        this.sepaMandateId.set('');
        this.sepaPath.set('sepa_authorization.pdf');
        if (inputElement) {
          inputElement.value = '';
        }

        await setDoc(
          doc(db, `/organisations/${this.organisationId}/users/${userId}`),
          {
            sepaMandateId: deleteField(),
          },
          { merge: true },
        );
      }
    });
  }

  async deleteSepa() {
    const userId = this.data.user?.id;
    if (!userId) {
      return;
    }
    const dialogRef = this.dialog.open(WarningDialog, {
      width: '600px',
      data: {
        dialogType: 'deleteSepaMandate',
      },
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        try {
          await setDoc(
            doc(db, `/organisations/${this.organisationId}/users/${userId}`),
            {
              sepaSignatureDate: deleteField(),
              sepaDownloadUrl: deleteField(),
              sepaPath: deleteField(),
              sepaMandateId: deleteField(),
            },
            { merge: true },
          );
          this.sepaMandateId.set('');
          this.sepaPath.set('sepa_authorization.pdf');
          console.log('sepa deleted');
          openSnackbar(this.snackBar, 'SEPA machtiging is verwijderd.');
        } catch (error) {
          console.log(error);
        }
      }
    });
  }

  generatePassword() {
    this.userForm.controls.password.setValue(generateRandomString());
  }
}
